// /src/components/data-display/SingleDataValue.jsx
import { memo, useMemo } from 'react';
import { Flex, Text, Link } from '@chakra-ui/react';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export const SingleDataValue = memo(({ id, label, value, mb, link, display, onClick, animation, hoverColor }) => {
  const { colors } = useThemedStyles();
  
  const styles = useMemo(() => ({
    label: {
      color: colors.text.label,
      hover: colors.text.labelHover
    },
    value: {
      color: colors.text.primary,
      hover: colors.text.primary
    }
  }), [colors]);
  
  const linkedFontSize = link ? '16px' : '20px';
  const LOADING_TEXT = 'Loading...'

  return (
    <Flex
      id={id}
      direction="column"
      gap="6px"
      minW="0"
      w="100%"
      textAlign="center"
      display={display}
      cursor="pointer"
      onClick={() => onClick(id)}
      animation={animation}
      transformOrigin="center center"
      borderRadius="md"
      p="0"
      overflow="visible"
      _hover={{
        '.label': { color: styles.label.hover },
        '.value': { color: styles.value.hover },
      }}
    >
      <Text
        className="label"
        fontSize={{ 
          base: "12px",
          xs: "12px",
          sm: "11px",
          sm2: "11px",
          md: "11px",
          lg: "11px",
          xl: "11px",
          '2xl': "12px"
        }}
        color={styles.label.color}
        fontWeight="700"
        lineHeight="1"
        textTransform="uppercase"
        whiteSpace="nowrap"
      >
        {label}
      </Text>

      <Flex align="center" justify={{ base: "center", md: "center" }}>
        {link ? (
          <Link href={link} isExternal>
            <Flex align="center">
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <Flex ml={2} alignItems="center">
                <Text
                  className="value"
                  fontSize={linkedFontSize}
                  fontWeight="700"
                  lineHeight="1"
                  align={{ base: 'center', md: 'unset' }}
                  mb={mb}
                  mr={2}
                  color={styles.value.color}
                  whiteSpace="nowrap"
                >
                  {value ?? LOADING_TEXT}
                </Text>
              </Flex>
            </Flex>
          </Link>
        ) : (
          <Text
            className="value"
            fontSize={{ 
              base: "18px",
              xs: "18px",
              sm: "20px",
              sm2: "22px",
              md: "24px",
              lg: "24px",
              xl: "24px",
              '2xl': "26px"
            }}
            fontWeight="700"
            lineHeight="1"
            mb={mb}
            color={styles.value.color}
            whiteSpace="nowrap"
          >
            {value ?? LOADING_TEXT}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if key props change
  return (
    prevProps.label === nextProps.label &&
    prevProps.value === nextProps.value &&
    prevProps.link === nextProps.link &&
    prevProps.animation === nextProps.animation
  );
});
